import { getApiClient } from "@src/api";
import { graphql } from "@src/gql/gql";
import { SortEnumType } from "@src/gql/graphql";
import { queryOptions, useQuery } from "@tanstack/react-query";
import { type MentionInboxPayload, mentionInboxPayloadSchema } from "../schema";
import { mentionsQueryKeys } from "./query-keys";

export const mentionInboxQuery = graphql(/* GraphQL */ `
	query MentionInbox(
		$first: Int
		$after: String
		$where: MentionInboxItemFilterInput
		$order: [MentionInboxItemSortInput!]
	) {
		mentions {
			inbox(
				first: $first
				after: $after
				where: $where
				order: $order
			) {
				pageInfo {
					hasNextPage
					endCursor
				}
				nodes {
					id
					idempotencyId
					authorId
					authorName
					authorAvatarUrl
					sourceId
					anchorId
					snippet
					deepLink
					createdAt
					isRead
					readAt
				}
			}
		}
	}
`);

export const mentionUnreadCountQuery = graphql(/* GraphQL */ `
	query MentionUnreadCount {
		mentions {
			unreadCount
		}
	}
`);

export type MentionInboxParams = {
	unreadOnly?: boolean;
	first?: number;
	after?: string;
};

export async function fetchMentionInbox(
	params?: MentionInboxParams,
): Promise<MentionInboxPayload> {
	const client = getApiClient();
	const unreadOnly = params?.unreadOnly ?? false;
	const where = unreadOnly ? { isRead: { eq: false } } : undefined;

	const response = await client.query({
		query: mentionInboxQuery,
		variables: {
			first: params?.first ?? 50,
			after: params?.after,
			where,
			order: [{ createdAt: SortEnumType.Desc }],
		},
		fetchPolicy: "network-only",
	});

	const inbox = response.data?.mentions?.inbox;
	if (!inbox) {
		throw new Error("Mention inbox response missing data");
	}

	const nodes = inbox.nodes ?? [];
	return mentionInboxPayloadSchema.parse({
		items: nodes,
		hasMore: inbox.pageInfo.hasNextPage,
		nextCursor: inbox.pageInfo.endCursor,
	});
}

export async function fetchMentionUnreadCount(): Promise<number> {
	const client = getApiClient();
	const response = await client.query({
		query: mentionUnreadCountQuery,
		fetchPolicy: "network-only",
	});

	const unreadCount = response.data?.mentions?.unreadCount;
	if (unreadCount === undefined || unreadCount === null) {
		throw new Error("Mention unread count response missing data");
	}

	return unreadCount;
}

export function mentionInboxQueryOptions(params?: MentionInboxParams) {
	return queryOptions({
		queryKey: mentionsQueryKeys.inbox({
			unreadOnly: params?.unreadOnly ?? false,
			first: params?.first,
			after: params?.after,
		}),
		queryFn: () => fetchMentionInbox(params),
		staleTime: 10 * 1000,
	});
}

export function mentionUnreadCountQueryOptions() {
	return queryOptions({
		queryKey: mentionsQueryKeys.unreadCount(),
		queryFn: fetchMentionUnreadCount,
		staleTime: 10 * 1000,
	});
}

export function useMentionInbox(params?: MentionInboxParams) {
	return useQuery(mentionInboxQueryOptions(params));
}

export function useMentionUnreadCount() {
	return useQuery(mentionUnreadCountQueryOptions());
}
